import { useTranslation } from "react-i18next";
import { TasksContent } from "@/components/tasks/TasksContent";
import { TasksFilterWrapper } from "@/components/tasks/TasksFilterWrapper";
import { TasksActions } from "@/components/tasks/TasksActions";
import { CreateTaskForm } from "@/components/tasks/CreateTaskForm";
import { Task } from "@/hooks/tasks/useTasks";
import { useMatchTasks } from "@/hooks/tasks/useMatchTasks";
import { TasksFilterProvider } from "@/contexts/TasksFilterContext";
import { usePersistedViewMode } from "@/utils/viewMode";
import { useState } from "react";
import { CheckSquare2 } from "lucide-react";

interface MatchTasksTabProps {
  matchId: string;
  candidateId?: string;
  candidateName?: string;
  jobId?: string;
  jobTitle?: string;
  companyId?: string;
  companyName?: string;
}

export const MatchTasksTab = ({ 
  matchId, 
  candidateId, 
  candidateName, 
  jobId, 
  jobTitle, 
  companyId, 
  companyName 
}: MatchTasksTabProps) => {
  const { t } = useTranslation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [viewMode, setViewMode] = usePersistedViewMode("match-tasks", "list");
  
  const { data: tasks = [], isLoading, error, refetch } = useMatchTasks(matchId);

  if (error) {
    return (
      <div className="text-center">
        <h2 className="text-xl font-semibold text-red-600 mb-2">
          {t("common.error", "Error")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t("tasks.error.loading", "Failed to load tasks. Please try again.")}
        </p>
      </div>
    );
  }

  return (
    <TasksFilterProvider viewMode={viewMode} setViewMode={setViewMode}>
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t("matching.tabs.tasks", "Tasks")}</h2>
            <p className="text-gray-500 dark:text-gray-400">{t("matching.tabs.tasksDescription", "All tasks linked to this match")}</p>
          </div>
          <TasksActions
            onCreateTask={() => setIsCreateDialogOpen(true)}
          />
        </div>
        
        <TasksFilterWrapper />
        
        <TasksContent tasks={tasks} isLoading={isLoading} onTaskUpdated={refetch} />
        
        <CreateTaskForm
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onSubmit={() => {
            setIsCreateDialogOpen(false);
            refetch();
          }}
          initialData={{
            relations: {
              candidates: candidateId ? [candidateId] : [],
              matches: [matchId],
              jobs: jobId ? [jobId] : [],
              companies: companyId ? [companyId] : [],
              calendar: []
            },
            // Pass all entity info for display
            ...(candidateId && candidateName && {
              _prelinkedCandidate: {
                id: candidateId,
                name: candidateName
              }
            }),
            ...(jobId && jobTitle && {
              _prelinkedJob: {
                id: jobId,
                title: jobTitle
              }
            }),
            ...(companyId && companyName && {
              _prelinkedCompany: {
                id: companyId,
                name: companyName
              }
            })
          }}
        />
      </div>
    </TasksFilterProvider>
  );
};
